/**
 * AnalysisInsightsPanel Component
 *
 * Live analysis insights aligned with adequacy, fairness, and assurance themes.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { dashboardApi, DashboardMetrics } from '../services/api';
import { useStore } from '../store';

const AnalysisInsightsPanel: React.FC = () => {
  const { solverConfig, solverResult } = useStore();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;

    const fetchMetrics = async () => {
      try {
        const data = await dashboardApi.getMetrics(solverConfig.load_factor);
        if (isActive) {
          setMetrics(data);
          setError(null);
        }
      } catch (e) {
        if (isActive) {
          setError(e instanceof Error ? e.message : 'Failed to load metrics');
        }
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 10000);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [solverConfig.load_factor]);

  const adequacyStats = useMemo(() => {
    const capacity = metrics?.grid.total_capacity_mw;
    const load =
      solverResult?.summary?.total_load_mw ?? metrics?.grid.total_load_mw;
    const reserveMarginPct =
      capacity && load !== undefined ? ((capacity - load) / capacity) * 100 : null;
    const maxStress = metrics?.regional_stress.reduce(
      (max, region) => Math.max(max, region.stress_level),
      0
    );
    const overloaded = solverResult
      ? solverResult.branch_results.filter((b) => b.loading_percent > 100).length
      : null;
    const highLoading = solverResult
      ? solverResult.branch_results.filter((b) => b.loading_percent > 80).length
      : null;
    return { capacity, load, reserveMarginPct, maxStress, overloaded, highLoading };
  }, [metrics, solverResult]);

  const fairnessStats = useMemo(() => {
    if (!metrics) return null;
    const sorted = [...metrics.regional_stress].sort(
      (a, b) => b.stress_level - a.stress_level
    );
    const hotspots = sorted.slice(0, 4);
    const stressedCount = metrics.regional_stress.filter(
      (region) => region.stress_level >= 0.8
    ).length;
    const northIsland = metrics.grid.islands.find((i) => i.name.includes('North'));
    const southIsland = metrics.grid.islands.find((i) => i.name.includes('South'));
    return { hotspots, stressedCount, northIsland, southIsland };
  }, [metrics]);

  const netZeroStats = useMemo(() => {
    if (!metrics) return null;
    const renewableTypes = new Set(['hydro', 'geothermal', 'wind', 'solar', 'battery']);
    const renewableShare = metrics.grid.generation_mix.reduce((sum, item) => {
      return sum + (renewableTypes.has(item.fuel_type) ? item.percentage : 0);
    }, 0);
    const fossilTypes = new Set(['gas', 'coal', 'diesel', 'cogen']);
    const fossilMix = metrics.grid.generation_mix.filter((item) => fossilTypes.has(item.fuel_type));
    const topFossil = fossilMix.sort((a, b) => b.percentage - a.percentage)[0];
    return {
      renewableShare,
      topFossil,
      hvdcFlow: metrics.grid.hvdc_flow_mw,
      hvdcDirection: metrics.grid.hvdc_direction,
      capacityFactor: metrics.grid.capacity_factor,
    };
  }, [metrics]);

  const scenarioSnapshot = useMemo(() => {
    const loadFactorPct = Math.round(solverConfig.load_factor * 100);
    const island = solverConfig.island || 'Full Grid';
    const lastUpdated = metrics ? new Date(metrics.timestamp).toLocaleTimeString() : null;
    return {
      loadFactorPct,
      island,
      solverType: solverConfig.solver_type,
      lastUpdated,
    };
  }, [solverConfig, metrics]);

  return (
    <>
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Adequacy & Reliability</h3>
        {error && <div className="text-sm text-red-400 mb-2">{error}</div>}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Total Capacity</span>
            <span className="text-white">
              {adequacyStats.capacity ? `${adequacyStats.capacity.toFixed(0)} MW` : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">System Load</span>
            <span className="text-white">
              {adequacyStats.load !== undefined ? `${adequacyStats.load.toFixed(0)} MW` : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Reserve Margin</span>
            <span
              className={
                adequacyStats.reserveMarginPct !== null &&
                adequacyStats.reserveMarginPct < 15
                  ? 'text-red-400'
                  : 'text-green-400'
              }
            >
              {adequacyStats.reserveMarginPct !== null
                ? `${adequacyStats.reserveMarginPct.toFixed(1)}%`
                : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Peak Regional Stress</span>
            <span className="text-white">
              {adequacyStats.maxStress !== undefined
                ? `${(adequacyStats.maxStress * 100).toFixed(0)}%`
                : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Lines {'>'}80%</span>
            <span className="text-white">
              {adequacyStats.highLoading !== null ? adequacyStats.highLoading : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Lines {'>'}100%</span>
            <span className="text-white">
              {adequacyStats.overloaded !== null ? adequacyStats.overloaded : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Fairness & Regional</h3>
        {!fairnessStats && <div className="text-sm text-gray-400">Loading regional signals...</div>}
        {fairnessStats && (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Regions {'>='} 80% stress</span>
              <span className="text-white">{fairnessStats.stressedCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">North Island stress</span>
              <span className="text-white">
                {fairnessStats.northIsland
                  ? `${(fairnessStats.northIsland.stress_level * 100).toFixed(0)}%`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">South Island stress</span>
              <span className="text-white">
                {fairnessStats.southIsland
                  ? `${(fairnessStats.southIsland.stress_level * 100).toFixed(0)}%`
                  : 'N/A'}
              </span>
            </div>
            <div className="pt-2 border-t border-gray-700">
              <div className="text-xs text-gray-400 mb-2">Top Stress Hotspots</div>
              <div className="space-y-1">
                {fairnessStats.hotspots.map((region) => (
                  <div key={region.region_code} className="flex justify-between">
                    <span className="text-gray-300">{region.region_code}</span>
                    <span className="text-white">{(region.stress_level * 100).toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Net-Zero Signals</h3>
        {!netZeroStats && <div className="text-sm text-gray-400">Loading grid mix...</div>}
        {netZeroStats && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Renewable Share</span>
              <span className="text-white">{netZeroStats.renewableShare.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Dominant Thermal</span>
              <span className="text-white">
                {netZeroStats.topFossil
                  ? `${netZeroStats.topFossil.fuel_type} (${netZeroStats.topFossil.percentage.toFixed(1)}%)`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Capacity Factor</span>
              <span className="text-white">{Math.round(netZeroStats.capacityFactor * 100)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">HVDC Flow</span>
              <span className="text-white">
                {netZeroStats.hvdcFlow.toFixed(0)} MW {netZeroStats.hvdcDirection === 'north' ? 'N' : 'S'}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Scenario Snapshot</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Solver</span>
            <span className="text-white">{scenarioSnapshot.solverType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Load Factor</span>
            <span className="text-white">{scenarioSnapshot.loadFactorPct}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Island</span>
            <span className="text-white">{scenarioSnapshot.island}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Metrics Updated</span>
            <span className="text-white">
              {scenarioSnapshot.lastUpdated || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default AnalysisInsightsPanel;
